import React, { CSSProperties } from 'react';
import styled, { css } from 'styled-components';
import { fontSizes } from '../../styles/fonts';
import { TNumericBadgeTheme } from './HBNumericBadge.types';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { vars } from '../../theme';

export type NumericBadgeProps = {
    /** Color scheme for the badge — `accent` uses blue, `neutral` uses gray. */
    theme: TNumericBadgeTheme;
    /** When provided, displays the number inside the badge (capped at `max`); when omitted, renders as a small dot indicator. */
    number?: number;
    /** Largest number shown as-is; anything above renders as `"{max}+"`. */
    max?: number;
    /** CSS position properties for absolutely positioning the badge relative to its parent. */
    position?: Pick<
        CSSProperties,
        'position' | 'top' | 'right' | 'bottom' | 'left' | 'transform'
    >;
    /** Color of the outline border around the badge; accepts any CSS color string. */
    borderColor?: string;
    /** Whether to render a 2px outline border around the badge, useful for separating it visually from its background. */
    hasBorder?: boolean;
    /** Controls badge visibility via opacity and pointer-events (not a scale transform). When false, the badge fades out and stops receiving pointer events; when true, it's fully visible and interactive. */
    visible?: boolean;
} & Partial<MustIncludeOneOfBasicTestIdProps>;

const THEME_COLORS: Record<
    TNumericBadgeTheme,
    {
        withNumber: { backgroundColor: string; textColor: string };
        withoutNumber: { backgroundColor: string; textColor: string };
    }
> = {
    accent: {
        withNumber: {
            backgroundColor: vars.colors.base.accent,
            textColor: vars.colors.text.invertedPrimary
        },
        withoutNumber: {
            backgroundColor: vars.colors.base.accent,
            textColor: vars.colors.text.invertedPrimary
        }
    },
    neutral: {
        withNumber: {
            backgroundColor: vars.colors.surface.itemStrong,
            textColor: vars.colors.text.primary
        },
        withoutNumber: {
            backgroundColor: vars.colors.surface.itemStrong,
            textColor: vars.colors.text.primary
        }
    },
    danger: {
        withNumber: {
            backgroundColor: vars.colors.base.danger,
            textColor: vars.colors.text.invertedPrimary
        },
        withoutNumber: {
            backgroundColor: vars.colors.base.danger,
            textColor: vars.colors.text.invertedPrimary
        }
    },
    warning: {
        withNumber: {
            backgroundColor: vars.colors.base.warning,
            textColor: vars.colors.text.invertedPrimary
        },
        withoutNumber: {
            backgroundColor: vars.colors.base.warning,
            textColor: vars.colors.text.invertedPrimary
        }
    }
};

const HBNumericBadge = ({
    number,
    max = 99,
    position,
    theme,
    hasBorder = true,
    borderColor = vars.colors.surface.container,
    visible = true,
    testId,
    testIdName
}: NumericBadgeProps) => {
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'badge',
        componentName: 'HBNumericBadge'
    });
    const displayNumber = number && number > max ? `${max}+` : number;

    return (
        <NumericBadge
            data-testid={finalTestId}
            theme={theme}
            number={number}
            position={position}
            hasBorder={hasBorder}
            borderColor={borderColor}
            visible={visible}
        >
            <InnerNumber>{displayNumber}</InnerNumber>
        </NumericBadge>
    );
};

const badgeSize = '8px';
const numberedBadgeSize = '16px';

export default HBNumericBadge;

const NumericBadge = styled.div<NumericBadgeProps>`
    min-width: ${props =>
        props.number !== undefined ? numberedBadgeSize : badgeSize};
    height: ${props =>
        props.number !== undefined ? numberedBadgeSize : badgeSize};
    color: ${props =>
        props.number !== undefined
            ? THEME_COLORS[props.theme].withNumber.textColor
            : THEME_COLORS[props.theme].withoutNumber.textColor};
    font-size: ${fontSizes.s12};
    font-family: ${vars.fonts.body};
    font-weight: 600;
    line-height: ${numberedBadgeSize};
    display: inline-flex;
    padding: 0 4px;
    align-items: center;
    justify-content: center;
    border-radius: ${props =>
        props.number !== undefined ? vars.radius.xs : vars.radius.full};
    ${({ hasBorder, borderColor }) =>
        hasBorder &&
        css`
            outline: solid 2px ${borderColor};
        `}
    background-color: ${props =>
        props.number !== undefined
            ? THEME_COLORS[props.theme].withNumber.backgroundColor
            : THEME_COLORS[props.theme].withoutNumber.backgroundColor};
    ${({ position }) => position}
    opacity: ${props => (props.visible ? 1 : 0)};
    pointer-events: ${props => (props.visible ? 'auto' : 'none')};
    transition: opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
`;

const InnerNumber = styled.span`
    position: relative;
    bottom: -0.5px;
    text-rendering: geometricPrecision;
`;
